import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Alert
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import axios from '../../api/axios';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import SharedPageBackground from '../../components/SharedPageBackground/SharedPageBackground';

// 暂时禁用的课程（还没有题目的课程）
const DISABLED_COURSES = [];  // 课程代码列表
// ============================================

// 随机图片生成器
const getRandomImage = () => {
  const images = [
    'https://picsum.photos/seed/course1/400/200',
    'https://picsum.photos/seed/course2/400/200',
    'https://picsum.photos/seed/course3/400/200',
    'https://picsum.photos/seed/course4/400/200',
    'https://picsum.photos/seed/course5/400/200',
  ];
  return images[Math.floor(Math.random() * images.length)];
};

// FeaturePost 组件 - 用于展示主推内容（海报/横幅）
const FeaturePost = ({ image, imageText, title, description, classid, t, isTeacher, onEdit }) => {
  return (
    <Paper
      sx={{
        position: 'relative',
        backgroundColor: 'grey.800',
        color: '#fff',
        mt: 3,
        mb: 4,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundImage: `url(${image})`,
      }}
    >
      {<img style={{ display: 'none' }} src={image} alt={imageText} />}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          left: 0,
          backgroundColor: 'rgba(0,0,0,.4)',
        }}
      />
      <Grid container>
        <Grid item md={6}>
          <Box
            sx={{
              position: 'relative',
              p: { xs: 3, md: 6 },
              pr: { md: 0 },
            }}
          >
            {isTeacher && onEdit && (
              <IconButton
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
                size="small"
              >
                <EditIcon />
              </IconButton>
            )}
            <Typography
              component="h1"
              variant="h3"
              color="inherit"
              gutterBottom
              fontWeight="bold"
            >
              {title}
            </Typography>
            <Typography
              variant="h5"
              color="inherit"
              paragraph
              sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                lineHeight: '1.5em',
                maxHeight: '4.5em',
              }}
            >
              {description}
            </Typography>
            {classid && (
              <Typography variant="body1" sx={{ mt: 2 }}>
                {t('courseSelection.courseCode')}: {classid}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

function CourseSelection() {
  const { user, selectCourse } = useContext(AuthContext);
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]); // User's enrolled courses
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addCourseDialogOpen, setAddCourseDialogOpen] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [selectedCourseToAdd, setSelectedCourseToAdd] = useState(null);
  const [passcode, setPasscode] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState('');
  const [createCourseDialogOpen, setCreateCourseDialogOpen] = useState(false);
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [createCourseError, setCreateCourseError] = useState('');
  const [newCourse, setNewCourse] = useState({
    code: '',
    name: '',
    description: '',
    studentPasscode: ''
  });
  const [editCourseDialogOpen, setEditCourseDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(false);
  const [editCourseError, setEditCourseError] = useState('');
  const [editingCourseData, setEditingCourseData] = useState(null);
  const [banner, setBanner] = useState({
    enabled: true,
    image: 'https://picsum.photos/seed/csc108-feature/1200/400',
    title: '课程公告',
    description: '在此编辑公告内容',
    classId: null
  });
  const [editBannerDialogOpen, setEditBannerDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(false);
  const [editBannerForm, setEditBannerForm] = useState({ image: '', title: '', description: '', classId: '' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [deletingCourse, setDeletingCourse] = useState(false);
  const [deleteCourseError, setDeleteCourseError] = useState('');
  const navigate = useNavigate();
  
  // 为每个课程生成固定的随机图片
  const courseImages = useMemo(() => {
    return {};
  }, []);

  useEffect(() => {
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await axios.get('/courses/banner');
        setBanner(res.data || { enabled: true, image: '', title: '', description: '', classId: null });
      } catch (e) {
        console.warn('Failed to fetch banner:', e);
      }
    };
    fetchBanner();
  }, []);

  const fetchCourses = async () => {
    try {
      // Fetch user's enrolled courses
      const response = await axios.get('/enrollments');
      
      // 调试日志
      console.log('Enrollments API response:', response);
      console.log('Enrollments API response.data:', response.data);
      
      // 检查响应是否为 HTML（ngrok 警告页面）
      if (typeof response?.data === 'string' && response.data.includes('<!DOCTYPE html>')) {
        console.error('Received HTML instead of JSON - ngrok warning page detected');
        setError(t('courseSelection.ngrokWarning', { defaultValue: 'ngrok warning page detected. Please check backend configuration or upgrade ngrok to bypass warning page.' }));
        setCourses([]);
        return;
      }
      
      // 确保数据是数组格式
      let coursesData = response?.data || [];
      
      // 检查响应状态
      if (!response || response.status !== 200) {
        console.error('Invalid response status:', response?.status);
        setCourses([]);
        return;
      }
      
      // 如果 response.data 不是数组，尝试从响应中提取数组
      if (!Array.isArray(coursesData)) {
        console.warn('API returned non-array data, attempting to extract:', coursesData);
        coursesData = coursesData?.courses || coursesData?.data || [];
      }
      
      // 最终检查：确保 coursesData 是数组
      if (!Array.isArray(coursesData)) {
        console.error('API returned non-array data after extraction:', coursesData);
        coursesData = [];
      }
      
      console.log('Final coursesData:', coursesData);
      setCourses(coursesData);
      
      // 为每个课程分配固定的图片（只在有数据时）
      if (Array.isArray(coursesData) && coursesData.length > 0) {
        coursesData.forEach(course => {
          if (course && !courseImages[course.id]) {
            courseImages[course.id] = getRandomImage();
          }
        });
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      const errorDetails = {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      };
      console.error('Error details:', errorDetails);
      
      // 设置错误信息以便显示给用户
      if (error.response?.status === 401) {
        setError(t('courseSelection.error.unauthorized', { defaultValue: 'Authentication failed, please login again' }));
      } else if (error.response?.status === 500) {
        setError(t('courseSelection.error.serverError', { defaultValue: 'Server error, please try again later' }));
      } else if (error.message === 'Network Error' || error.code === 'ECONNABORTED') {
        setError(t('courseSelection.error.networkError', { defaultValue: 'Network connection failed, please check if backend server is running' }));
      } else {
        setError(t('courseSelection.error.fetchFailed', { defaultValue: 'Failed to fetch courses list' }) + `: ${error.response?.data?.error || error.message}`);
      }
      
      // 发生错误时，确保 courses 是空数组
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddCourseDialog = async () => {
    setAddCourseDialogOpen(true);
    setSelectedCourseToAdd(null);
    setPasscode('');
    setEnrollError('');
    setLoadingAvailable(true);
    
    try {
      const response = await axios.get('/enrollments/available');
      setAvailableCourses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching available courses:', error);
      setEnrollError(t('courseSelection.error.fetchAvailable', { defaultValue: 'Failed to fetch available courses' }));
    } finally {
      setLoadingAvailable(false);
    }
  };

  const handleCloseAddCourseDialog = () => {
    setAddCourseDialogOpen(false);
    setSelectedCourseToAdd(null);
    setPasscode('');
    setEnrollError('');
  };

  const handleEnroll = async () => {
    if (!selectedCourseToAdd) {
      setEnrollError(t('courseSelection.error.selectCourse', { defaultValue: 'Please select a course' }));
      return;
    }

    if (user.role === 'student' && !passcode.trim()) {
      setEnrollError(t('courseSelection.error.passcodeRequired', { defaultValue: 'Passcode is required' }));
      return;
    }

    setEnrolling(true);
    setEnrollError('');

    try {
      await axios.post(`/enrollments/${selectedCourseToAdd.courseOfferingId || selectedCourseToAdd.id}`, {
        passcode: passcode.trim()
      });

      // Refresh courses list
      await fetchCourses();
      handleCloseAddCourseDialog();
    } catch (error) {
      console.error('Error enrolling in course:', error);
      setEnrollError(
        error.response?.data?.error || 
        t('courseSelection.error.enrollFailed', { defaultValue: 'Failed to enroll in course' })
      );
    } finally {
      setEnrolling(false);
    }
  };

  const handleOpenCreateCourseDialog = () => {
    setCreateCourseDialogOpen(true);
    setNewCourse({
      code: '',
      name: '',
      description: '',
      studentPasscode: ''
    });
    setCreateCourseError('');
  };

  const handleCloseCreateCourseDialog = () => {
    setCreateCourseDialogOpen(false);
    setNewCourse({
      code: '',
      name: '',
      description: '',
      studentPasscode: ''
    });
    setCreateCourseError('');
  };

  const handleCreateCourse = async () => {
    if (!newCourse.code.trim() || !newCourse.name.trim()) {
      setCreateCourseError('课程代码和课程名称是必需的');
      return;
    }

    setCreatingCourse(true);
    setCreateCourseError('');

    try {
      const response = await axios.post('/courses', {
        code: newCourse.code.trim(),
        name: newCourse.name.trim(),
        description: newCourse.description.trim() || null,
        studentPasscode: newCourse.studentPasscode.trim() || null
      });

      // Refresh courses list
      await fetchCourses();
      handleCloseCreateCourseDialog();
      
      // Show success message (optional)
      console.log('Course created successfully:', response.data);
    } catch (error) {
      console.error('Error creating course:', error);
      setCreateCourseError(
        error.response?.data?.error || 
        '创建课程失败，请重试'
      );
    } finally {
      setCreatingCourse(false);
    }
  };

  const handleOpenEditCourseDialog = (course) => {
    setEditingCourseData({
      id: course.id,
      offeringId: course.offeringId,
      code: course.code,
      name: course.name,
      description: course.description || '',
      studentPasscode: course.studentPasscode || ''
    });
    setEditCourseDialogOpen(true);
    setEditCourseError('');
  };

  const handleOpenDeleteConfirm = (course, e) => {
    e?.stopPropagation();
    setCourseToDelete(course);
    setDeleteCourseError('');
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setCourseToDelete(null);
    setDeleteCourseError('');
  };

  const handleDeleteCourse = async () => {
    if (!courseToDelete?.offeringId) return;
    setDeletingCourse(true);
    setDeleteCourseError('');
    try {
      await axios.delete(`/enrollments/offering/${courseToDelete.offeringId}`);
      await fetchCourses();
      handleCloseDeleteConfirm();
    } catch (error) {
      console.error('Error deleting course:', error);
      setDeleteCourseError(
        error.response?.data?.error || '删除课程失败，请重试'
      );
    } finally {
      setDeletingCourse(false);
    }
  };

  const handleCloseEditCourseDialog = () => {
    setEditCourseDialogOpen(false);
    setEditingCourseData(null);
    setEditCourseError('');
  };

  const handleOpenEditBannerDialog = () => {
    setEditBannerForm({
      image: banner.image || '',
      title: banner.title || '',
      description: banner.description || '',
      classId: banner.classId ?? ''
    });
    setEditBannerDialogOpen(true);
  };

  const handleCloseEditBannerDialog = () => {
    setEditBannerDialogOpen(false);
  };

  const handleUpdateBanner = async () => {
    setEditingBanner(true);
    try {
      const res = await axios.put('/courses/banner', {
        enabled: banner.enabled,
        image: editBannerForm.image.trim() || undefined,
        title: editBannerForm.title.trim(),
        description: editBannerForm.description.trim(),
        classId: editBannerForm.classId.trim() || null
      });
      setBanner(res.data);
      handleCloseEditBannerDialog();
    } catch (e) {
      console.error('Failed to update banner:', e);
    } finally {
      setEditingBanner(false);
    }
  };

  const handleUpdateCourse = async () => {
    if (!editingCourseData || !editingCourseData.name.trim()) {
      setEditCourseError('课程名称不能为空');
      return;
    }

    setEditingCourse(true);
    setEditCourseError('');

    try {
      const payload = {
        name: editingCourseData.name.trim(),
        description: editingCourseData.description.trim() || null
      };
      if (editingCourseData.offeringId) {
        payload.offeringId = editingCourseData.offeringId;
      }
      if (editingCourseData.studentPasscode !== undefined) {
        payload.studentPasscode = editingCourseData.studentPasscode.trim() || null;
      }
      await axios.put(`/courses/${editingCourseData.id}`, payload);

      // Refresh courses list
      await fetchCourses();
      handleCloseEditCourseDialog();
    } catch (error) {
      console.error('Error updating course:', error);
      setEditCourseError(
        error.response?.data?.error || 
        '更新课程失败，请重试'
      );
    } finally {
      setEditingCourse(false);
    }
  };


  const handleCourseSelect = (course) => {
    selectCourse(course);
    // Navigate based on role
    if (user.role === 'student') {
      navigate('/dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#000000', position: 'relative' }}>
        <SharedPageBackground />
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#000000', position: 'relative' }}>
      <SharedPageBackground />
      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
      {/* 顶部：Home + 语言切换（无完整 header） */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2, pb: 1 }}>
        <Button size="small" onClick={() => navigate('/')} sx={{ color: 'inherit', textTransform: 'none' }}>
          Home
        </Button>
        <LanguageSwitcher />
      </Box>

      {/* Course Cards */}
      <Container maxWidth="lg" sx={{ mt: 4, pb: 4 }}>
        {error && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
            <Typography variant="body1" fontWeight="bold">{t('common.error')}:</Typography>
            <Typography variant="body2">{error}</Typography>
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              {t('courseSelection.checkConsole', { defaultValue: 'Please open browser Developer Tools (F12) Console tab to view details' })}
            </Typography>
          </Box>
        )}
        {/* Feature Post - 展示主推内容（海报/横幅），新老用户一致 */}
        {banner.enabled && (
          <FeaturePost
            image={banner.image || 'https://picsum.photos/seed/csc108-feature/1200/400'}
            imageText={banner.title}
            title={banner.title}
            description={banner.description}
            classid={banner.classId}
            t={t}
            isTeacher={user?.role === 'teacher'}
            onEdit={handleOpenEditBannerDialog}
          />
        )}

        {/* All Courses - 与添加课程按钮同一行，新用户也可添加课程 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            {t('courseSelection.allCourses')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {user.role === 'teacher' && (
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateCourseDialog}
                sx={{ textTransform: 'none' }}
              >
                新建课程
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenAddCourseDialog}
              sx={{ textTransform: 'none' }}
            >
              {t('courseSelection.addCourse', { defaultValue: 'Add Course' })}
            </Button>
          </Box>
        </Box>

        {!Array.isArray(courses) || courses.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              {t('courseSelection.noCoursesYet', { defaultValue: '暂无课程，点击上方添加课程开始' })}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
                  {Array.isArray(courses) ? courses.map((course) => {
                    if (!course || !course.id) {
                      console.warn('Invalid course data:', course);
                      return null;
                    }
                    const isDisabled = DISABLED_COURSES.includes(course.code);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={course.id}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.3s',
                          opacity: isDisabled ? 0.6 : 1,
                          '&:hover': isDisabled ? {} : {
                            transform: 'translateY(-8px)',
                            boxShadow: 6
                          }
                        }}
                      >
                        <CardMedia
                          sx={{ 
                            height: 140,
                            filter: isDisabled ? 'grayscale(50%)' : 'none'
                          }}
                          image={courseImages[course.id] || getRandomImage()}
                          title={course.name}
                        />
                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography gutterBottom variant="h5" component="div" fontWeight="bold">
                            {course.name}{course.termCode ? ` (${course.termCode})` : ''}
                            {isDisabled && (
                              <Chip 
                                label={t('courseSelection.comingSoon')} 
                                size="small" 
                                sx={{ ml: 1 }} 
                                color="default"
                              />
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {course.code}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{
                              flexGrow: 1,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {course.description || t('courseSelection.defaultDescription', { defaultValue: 'This course guides you through the fundamental concepts of programming.' })}
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2, mt: 'auto' }}>
                          <Button
                            size="small"
                            onClick={() => !isDisabled && handleCourseSelect(course)}
                            disabled={isDisabled}
                          >
                            {isDisabled ? t('courseSelection.comingSoon') : t('courseSelection.enterCourse')}
                          </Button>
                          {user.role === 'teacher' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditCourseDialog(course)}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenDeleteConfirm(course, e)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </CardActions>
                      </Card>
                      </Grid>
                    );
                  }).filter(Boolean) : null}
            </Grid>
        )}

        {/* Create Course Dialog (Teacher Only) */}
        {user.role === 'teacher' && (
          <Dialog 
            open={createCourseDialogOpen} 
            onClose={handleCloseCreateCourseDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              新建课程
            </DialogTitle>
            <DialogContent>
              {createCourseError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {createCourseError}
                </Alert>
              )}
              
              <TextField
                fullWidth
                label="课程代码 *"
                value={newCourse.code}
                onChange={(e) => setNewCourse({ ...newCourse, code: e.target.value })}
                margin="normal"
                placeholder="例如: CSC108"
                helperText="课程代码将自动转换为大写"
                required
                autoFocus
              />
              
              <TextField
                fullWidth
                label="课程名称 *"
                value={newCourse.name}
                onChange={(e) => setNewCourse({ ...newCourse, name: e.target.value })}
                margin="normal"
                placeholder="例如: Introduction to Computer Programming"
                required
              />
              
              <TextField
                fullWidth
                label="课程描述"
                value={newCourse.description}
                onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                margin="normal"
                placeholder="可选：输入课程描述"
                multiline
                rows={3}
              />
              
              <TextField
                fullWidth
                label="学生密码"
                value={newCourse.studentPasscode}
                onChange={(e) => setNewCourse({ ...newCourse, studentPasscode: e.target.value })}
                margin="normal"
                placeholder="可选：留空将自动生成密码"
                helperText="留空将自动生成格式为：课程代码_学期代码_PASS"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCreateCourseDialog}>
                取消
              </Button>
              <Button
                onClick={handleCreateCourse}
                variant="contained"
                disabled={!newCourse.code.trim() || !newCourse.name.trim() || creatingCourse}
              >
                {creatingCourse ? '创建中...' : '创建课程'}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Edit Course Dialog (Teacher Only) */}
        {user.role === 'teacher' && editingCourseData && (
          <Dialog 
            open={editCourseDialogOpen} 
            onClose={handleCloseEditCourseDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              编辑课程
            </DialogTitle>
            <DialogContent>
              {editCourseError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {editCourseError}
                </Alert>
              )}
              
              <TextField
                fullWidth
                label="课程代码"
                value={editingCourseData.code || ''}
                margin="normal"
                disabled
                helperText="课程代码不可修改"
              />
              
              <TextField
                fullWidth
                label="课程名称 *"
                value={editingCourseData.name}
                onChange={(e) => setEditingCourseData({ ...editingCourseData, name: e.target.value })}
                margin="normal"
                placeholder="例如: Introduction to Computer Programming"
                required
                autoFocus
              />
              
              <TextField
                fullWidth
                label="课程描述"
                value={editingCourseData.description}
                onChange={(e) => setEditingCourseData({ ...editingCourseData, description: e.target.value })}
                margin="normal"
                placeholder="可选：输入课程描述"
                multiline
                rows={3}
              />
              
              <TextField
                fullWidth
                label="访问码 (Access Code)"
                value={editingCourseData.studentPasscode || ''}
                onChange={(e) => setEditingCourseData({ ...editingCourseData, studentPasscode: e.target.value })}
                margin="normal"
                placeholder="学生选课时需输入的访问码"
                helperText="留空则保持原访问码不变"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEditCourseDialog}>
                取消
              </Button>
              <Button
                onClick={handleUpdateCourse}
                variant="contained"
                disabled={!editingCourseData.name.trim() || editingCourse}
              >
                {editingCourse ? '保存中...' : '保存'}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Delete Course Confirmation Dialog (Teacher Only) */}
        {user.role === 'teacher' && (
          <Dialog
            open={deleteConfirmOpen}
            onClose={handleCloseDeleteConfirm}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>确认删除课程</DialogTitle>
            <DialogContent>
              {deleteCourseError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {deleteCourseError}
                </Alert>
              )}
              <Typography>
                确定要从您的课程列表中移除此课程 <strong>"{courseToDelete?.name}{courseToDelete?.termCode ? ` (${courseToDelete.termCode})` : ''}"</strong> 吗？
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                此操作将把该课程从您的列表中移除，已选课的学生不受影响。
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDeleteConfirm} disabled={deletingCourse}>
                取消
              </Button>
              <Button
                onClick={handleDeleteCourse}
                variant="contained"
                color="error"
                disabled={deletingCourse}
                startIcon={<DeleteIcon />}
              >
                {deletingCourse ? '删除中...' : '确认删除'}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Edit Banner Dialog (Teacher Only) */}
        {user?.role === 'teacher' && (
          <Dialog
            open={editBannerDialogOpen}
            onClose={handleCloseEditBannerDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>编辑课程公告</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                label="标题"
                value={editBannerForm.title}
                onChange={(e) => setEditBannerForm((f) => ({ ...f, title: e.target.value }))}
                margin="normal"
                placeholder="例如：12月18号考试"
              />
              <TextField
                fullWidth
                label="描述"
                value={editBannerForm.description}
                onChange={(e) => setEditBannerForm((f) => ({ ...f, description: e.target.value }))}
                margin="normal"
                placeholder="公告详细内容"
                multiline
                rows={4}
              />
              <TextField
                fullWidth
                label="背景图片 URL"
                value={editBannerForm.image}
                onChange={(e) => setEditBannerForm((f) => ({ ...f, image: e.target.value }))}
                margin="normal"
                placeholder="https://..."
              />
              <TextField
                fullWidth
                label="课程代码（可选）"
                value={editBannerForm.classId}
                onChange={(e) => setEditBannerForm((f) => ({ ...f, classId: e.target.value }))}
                margin="normal"
                placeholder="留空则不显示"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEditBannerDialog}>取消</Button>
              <Button onClick={handleUpdateBanner} variant="contained" disabled={editingBanner}>
                {editingBanner ? '保存中...' : '保存'}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Add Course Dialog */}
        <Dialog 
          open={addCourseDialogOpen} 
          onClose={handleCloseAddCourseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {t('courseSelection.addCourse', { defaultValue: 'Add Course' })}
          </DialogTitle>
          <DialogContent>
            {enrollError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {enrollError}
              </Alert>
            )}
            
            {loadingAvailable ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : availableCourses.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('courseSelection.noAvailableCourses', { defaultValue: 'No available courses to add' })}
              </Typography>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t('courseSelection.selectCourseToAdd', { defaultValue: 'Select a course to add:' })}
                </Typography>
                <List>
                  {availableCourses.map((course) => (
                    <ListItem key={course.courseOfferingId || course.id} disablePadding>
                      <ListItemButton
                        selected={selectedCourseToAdd?.courseOfferingId === (course.courseOfferingId || course.id)}
                        onClick={() => setSelectedCourseToAdd(course)}
                      >
                        <ListItemText
                          primary={`${course.courseName || course.name}${course.termCode ? ` (${course.termCode})` : ''}`}
                          secondary={course.courseCode || course.code}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>

                {user.role === 'student' && selectedCourseToAdd && (
                  <TextField
                    fullWidth
                    label={t('courseSelection.passcodeLabel', { defaultValue: 'Passcode' })}
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    margin="normal"
                    type="password"
                    placeholder={t('courseSelection.passcodePlaceholder', { defaultValue: 'Enter course passcode' })}
                    autoFocus
                  />
                )}

                {user.role === 'teacher' && selectedCourseToAdd && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {t('courseSelection.teacherNoPasscode', { defaultValue: 'Teachers can add courses without a passcode' })}
                  </Alert>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddCourseDialog}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleEnroll}
              variant="contained"
              disabled={!selectedCourseToAdd || enrolling || (user.role === 'student' && !passcode.trim())}
            >
              {enrolling 
                ? t('courseSelection.enrolling', { defaultValue: 'Adding...' })
                : t('courseSelection.add', { defaultValue: 'Add' })}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      </Box>
    </Box>
  );
}

export default CourseSelection;

